import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_EQUIPMENT_STATUSES, EQUIPMENT_STATUS, type EquipmentView } from '@lims/shared-types';
import { ApiError } from '../../api/client.js';
import { equipmentApi, usersApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  CheckboxField,
  ConfirmDialog,
  DataTable,
  DateField,
  Dialog,
  FilterBar,
  PageHeader,
  SearchField,
  SelectField,
  StatusBadge,
  SwitchField,
  TextAreaField,
  TextField,
  useToast,
  type DataColumn,
  SaveErrorNotice,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { equipmentName, formatCalendarDate } from '../work/status.js';
import { RegisterPanel } from '../reports/register-panel.js';
import { CalibrationBadge, calibrationState, useEquipmentNoticeDays } from './calibration-state.js';

type DialogState =
  | { readonly kind: 'create' }
  | { readonly kind: 'edit'; readonly item: EquipmentView }
  | { readonly kind: 'status'; readonly item: EquipmentView }
  | { readonly kind: 'calibration'; readonly item: EquipmentView }
  | null;

/**
 * The minimal equipment register — decision D85.
 *
 * A number, a name, a status and the date a calibration is valid until. That
 * is all this phase keeps; certificates, calibration history and maintenance
 * arrive with the full equipment module. Nothing here says which tests an
 * instrument serves: that is not known (limitation 26).
 *
 * USABILITY IS THE BACKEND'S VERDICT, rendered rather than recomputed, so the
 * page can never promise an instrument the server will refuse.
 *
 * THE CREATE FORM DOES NOT ASK WHETHER CALIBRATION IS REQUIRED. The backend
 * applies its default to a new entry, and restating that default here would
 * put the same policy in two places (`CLAUDE.md` section 6.1). It is changed
 * afterwards, on the entry itself, where the value shown is the server's.
 */
export function EquipmentPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const noticeDays = useEquipmentNoticeDays();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [usableOnly, setUsableOnly] = useState(false);
  const [calibrationDueBy, setCalibrationDueBy] = useState<string | null>(null);
  const [responsibleUserId, setResponsibleUserId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<DialogState>(null);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [calibrationRequired, setCalibrationRequired] = useState(true);
  const [validUntil, setValidUntil] = useState<string | null>(null);
  const [nextStatus, setNextStatus] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const list = useQuery({
    queryKey: [
      'equipment',
      { search, status, usableOnly, calibrationDueBy, responsibleUserId, page },
    ],
    queryFn: () =>
      equipmentApi.list({
        page,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(usableOnly ? { usableOnly: 'true' } : {}),
        ...(calibrationDueBy ? { calibrationDueBy } : {}),
        ...(responsibleUserId ? { responsibleUserId } : {}),
      }),
  });

  // The register's own responsible-person filter reads the same active-staff
  // list the detail page uses to assign one — only when this caller may read
  // users at all (a courtesy: the filter is simply absent otherwise, and the
  // server's own check is what actually protects the list, CLAUDE.md 5.1).
  const mayReadUsers = can(PERMISSIONS.USERS_VIEW);
  const users = useQuery({
    queryKey: ['users', 'for-equipment-responsibility'],
    queryFn: () => usersApi.list({ includeInactive: false, pageSize: 200 }),
    enabled: mayReadUsers,
  });

  const open = (next: DialogState) => {
    const item = next && next.kind !== 'create' ? next.item : null;
    setCode(item?.equipmentCode ?? '');
    setName(item?.name ?? '');
    setNotes(item?.notes ?? '');
    setCalibrationRequired(item?.calibrationRequired ?? true);
    setValidUntil(item?.calibrationValidUntil ?? null);
    setNextStatus(item?.status ?? null);
    setReason('');
    setDialog(next);
  };

  const orNull = (value: string) => (value.trim() === '' ? null : value.trim());

  const save = useMutation({
    mutationFn: async () => {
      if (!dialog) return null;
      if (dialog.kind === 'create') {
        return equipmentApi.create({
          equipmentCode: code.trim(),
          name: name.trim(),
          notes: orNull(notes),
          ...(validUntil ? { calibrationValidUntil: validUntil } : {}),
        });
      }
      if (dialog.kind === 'edit') {
        return equipmentApi.update(dialog.item.id, {
          equipmentCode: code.trim(),
          name: name.trim(),
          notes: orNull(notes),
          calibrationRequired,
        });
      }
      if (dialog.kind === 'status') {
        return equipmentApi.setStatus(dialog.item.id, {
          status: nextStatus,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        });
      }
      return equipmentApi.setCalibration(dialog.item.id, {
        calibrationValidUntil: validUntil,
        ...(reason.trim() ? { note: reason.trim() } : {}),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['equipment'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      show({ tone: 'success', message: t('equipment.saved') });
      setDialog(null);
    },
  });

  const mayManage = can(PERMISSIONS.EQUIPMENT_MANAGE);
  const mayCalibrate = can(PERMISSIONS.CALIBRATION_MANAGE);

  // Delete (D329): offered only for an instrument nothing has ever cited
  // (`canDelete`); the server checks again and refuses one in use.
  const [deleting, setDeleting] = useState<EquipmentView | null>(null);
  const remove = useMutation({
    mutationFn: (item: EquipmentView) => equipmentApi.remove(item.id),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['equipment'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      show({ tone: 'success', message: t('equipment.deleted') });
    },
    onError: async (error) => {
      // Something cited it since the list was loaded: show the real state.
      if (error instanceof ApiError && error.status === 409) {
        await queryClient.invalidateQueries({ queryKey: ['equipment'] });
      }
    },
  });

  const columns: readonly DataColumn<EquipmentView>[] = [
    {
      key: 'code',
      header: t('equipment.codeLabel'),
      render: (item) => (
        <div className="min-w-0">
          <Link
            to={`/equipment/${item.id}`}
            className="measure block font-semibold text-primary-700 underline-offset-2 hover:underline"
            dir="ltr"
          >
            {item.equipmentCode}
          </Link>
          <span className="block truncate text-caption text-default-600">
            {equipmentName(item)}
          </span>
          {item.serialNumber ? (
            <span className="block truncate text-caption text-default-600" dir="ltr">
              {t('equipment.serialNumberShort', { value: item.serialNumber })}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'responsible',
      header: t('equipment.responsibleColumn'),
      render: (item) => (
        <span className="text-caption text-default-700">
          <bdi>{item.responsibleUser?.displayName ?? t('common.none')}</bdi>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (item) => (
        <div className="min-w-0">
          <StatusBadge
            label={t(`equipmentStatus.${item.status}`)}
            tone={item.status === EQUIPMENT_STATUS.IN_SERVICE ? 'neutral' : 'warning'}
          />
          {item.statusReason ? (
            <span className="mt-1 block truncate text-caption text-default-600">
              {item.statusReason}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'calibration',
      header: t('equipment.calibrationColumn'),
      // Validity at a glance (D278): expired or missing is danger, due within
      // the laboratory's notice period is warning — beside the date itself.
      render: (item) => (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <CalibrationBadge state={calibrationState(item, noticeDays)} />
          <span className="text-caption text-default-700">
            {!item.calibrationRequired
              ? t('equipment.calibrationNotRequired')
              : item.calibrationValidUntil
                ? t('equipment.validUntil', {
                    date: formatCalendarDate(item.calibrationValidUntil, locale),
                  })
                : t('equipment.noCalibrationRecorded')}
          </span>
        </div>
      ),
    },
    {
      key: 'usability',
      header: t('equipment.usabilityColumn'),
      render: (item) => (
        <StatusBadge
          label={
            item.usability.usable
              ? t('equipment.usableToday')
              : t(`equipmentBlock.${item.usability.blockReason ?? 'suspended'}`)
          }
          tone={item.usability.usable ? 'info' : 'danger'}
        />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (item) => (
        <div className="flex flex-wrap justify-end gap-inline">
          {mayCalibrate && item.status !== EQUIPMENT_STATUS.RETIRED ? (
            <ActionButton priority="tertiary" size="sm" onPress={() => open({ kind: 'calibration', item })}>
              {t('equipment.recordCalibration')}
            </ActionButton>
          ) : null}
          {mayManage && item.status !== EQUIPMENT_STATUS.RETIRED ? (
            <ActionButton priority="tertiary" size="sm" onPress={() => open({ kind: 'status', item })}>
              {t('equipment.changeStatus')}
            </ActionButton>
          ) : null}
          {mayManage ? (
            <ActionButton priority="tertiary" size="sm" onPress={() => open({ kind: 'edit', item })}>
              {t('common.edit')}
            </ActionButton>
          ) : null}
          {mayManage && item.canDelete ? (
            <ActionButton
              priority="tertiary"
              size="sm"
              onPress={() => {
                remove.reset();
                setDeleting(item);
              }}
              aria-label={t('equipment.deleteNamed', { code: item.equipmentCode })}
            >
              {t('equipment.delete')}
            </ActionButton>
          ) : null}
        </div>
      ),
    },
  ];

  const needsReason =
    dialog?.kind === 'status' && nextStatus !== null && nextStatus !== EQUIPMENT_STATUS.IN_SERVICE;
  const isNamed = name.trim() !== '';
  const canSave =
    dialog?.kind === 'create' || dialog?.kind === 'edit'
      ? code.trim() !== '' && isNamed
      : dialog?.kind === 'status'
        ? nextStatus !== null && (!needsReason || reason.trim().length >= 3)
        : true;

  return (
    <>
      <PageHeader
        title={t('equipment.title')}
        subtitle={t('equipment.subtitle')}
        breadcrumbs={[{ label: t('nav.equipment') }]}
        actions={
          mayManage ? (
            <ActionButton priority="primary" onPress={() => open({ kind: 'create' })}>
              {t('equipment.register')}
            </ActionButton>
          ) : null
        }
      />

      <RegisterPanel
        toolbar={
          <FilterBar>
            <SearchField
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              label={t('equipment.searchLabel')}
              placeholder={t('equipment.searchPlaceholder')}
            />
            <div className="w-full sm:w-48">
              <SelectField
                label={t('common.status')}
                value={status}
                onChange={(value) => {
                  setStatus(value);
                  setPage(1);
                }}
                placeholder={t('common.all')}
                options={ALL_EQUIPMENT_STATUSES.map((value) => ({
                  value,
                  label: t(`equipmentStatus.${value}`),
                }))}
              />
            </div>
            <CheckboxField
              label={t('equipment.usableOnly')}
              description={t('equipment.usableOnlyHint')}
              value={usableOnly}
              onChange={(value) => {
                setUsableOnly(value);
                setPage(1);
              }}
            />
            <div className="w-full sm:w-56">
              <DateField
                label={t('equipment.filters.calibrationDueBy')}
                description={t('equipment.filters.calibrationDueByHint')}
                value={calibrationDueBy}
                onChange={(value) => {
                  setCalibrationDueBy(value);
                  setPage(1);
                }}
              />
            </div>
            {mayReadUsers ? (
              <div className="w-full sm:w-56">
                <SelectField
                  label={t('equipment.filters.responsibleUser')}
                  value={responsibleUserId}
                  onChange={(value) => {
                    setResponsibleUserId(value);
                    setPage(1);
                  }}
                  placeholder={t('common.all')}
                  options={(users.data?.data ?? []).map((user) => ({
                    value: user.id,
                    label: user.displayName,
                  }))}
                />
              </div>
            ) : null}
          </FilterBar>
        }
      >
        <DataTable
          label={t('equipment.title')}
          columns={columns}
          rows={list.data?.data ?? []}
          rowKey={(item) => item.id}
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          emptyContent={
            search || status || usableOnly || calibrationDueBy || responsibleUserId
              ? t('equipment.noneFound')
              : t('equipment.noneYet')
          }
          page={page}
          totalPages={list.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </RegisterPanel>

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title={t('equipment.deleteTitle')}
        message={t('equipment.deleteMessage', {
          code: deleting?.equipmentCode ?? '',
          name: deleting?.name ?? '',
        })}
        confirmLabel={t('equipment.delete')}
        confirmPriority="destructive"
        isPending={remove.isPending}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
        }}
      >
        {remove.error ? (
          remove.error instanceof ApiError &&
          remove.error.details.some((d) => d.code === 'equipment_in_use') ? (
            <Alert tone="warning" title={t('equipment.inUse')} />
          ) : (
            <SaveErrorNotice error={remove.error} />
          )
        ) : null}
      </ConfirmDialog>
      <Dialog
        isOpen={dialog !== null}
        onClose={() => setDialog(null)}
        title={
          dialog?.kind === 'create'
            ? t('equipment.register')
            : dialog?.kind === 'status'
              ? t('equipment.changeStatus')
              : dialog?.kind === 'calibration'
                ? t('equipment.recordCalibration')
                : t('common.edit')
        }
        footer={
          <ActionBar>
            <ActionButton priority="tertiary" onPress={() => setDialog(null)} isDisabled={save.isPending}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={!canSave}
              isLoading={save.isPending}
              onPress={() => save.mutate()}
            >
              {t('common.save')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          {dialog?.kind === 'create' || dialog?.kind === 'edit' ? (
            <>
              <TextField
                label={t('equipment.codeLabel')}
                description={t('equipment.codeHint')}
                value={code}
                onChange={setCode}
                isRequired
              />
              <TextField
                label={t('equipment.nameLabel')}
                value={name}
                onChange={setName}
                textDirection="auto"
                isRequired
              />
              {dialog.kind === 'create' ? (
                <DateField
                  label={t('equipment.validUntilLabel')}
                  description={t('equipment.validUntilHint')}
                  value={validUntil}
                  onChange={setValidUntil}
                />
              ) : (
                <SwitchField
                  label={t('equipment.calibrationRequiredLabel')}
                  description={t('equipment.calibrationRequiredHint')}
                  value={calibrationRequired}
                  onChange={setCalibrationRequired}
                />
              )}
              <TextAreaField label={t('equipment.notesLabel')} value={notes} onChange={setNotes} rows={2} />
            </>
          ) : null}

          {dialog?.kind === 'status' ? (
            <>
              <SelectField
                label={t('common.status')}
                value={nextStatus}
                onChange={setNextStatus}
                isRequired
                options={ALL_EQUIPMENT_STATUSES.map((value) => ({
                  value,
                  label: t(`equipmentStatus.${value}`),
                }))}
              />
              {nextStatus === EQUIPMENT_STATUS.RETIRED ? (
                <Alert tone="warning" title={t('equipment.retireFinalTitle')} description={t('equipment.retireFinalBody')} />
              ) : null}
              <TextAreaField
                label={t('common.reason')}
                description={needsReason ? t('equipment.statusReasonRequired') : t('common.optional')}
                value={reason}
                onChange={setReason}
                isRequired={needsReason}
                rows={2}
              />
            </>
          ) : null}

          {dialog?.kind === 'calibration' ? (
            <>
              <Alert tone="info" title={t('equipment.calibrationNoticeTitle')} description={t('equipment.calibrationNoticeBody')} />
              <DateField
                label={t('equipment.validUntilLabel')}
                description={t('equipment.validUntilClearHint')}
                value={validUntil}
                onChange={setValidUntil}
              />
              <TextAreaField label={t('equipment.calibrationNoteLabel')} value={reason} onChange={setReason} rows={2} />
            </>
          ) : null}

          {save.error ? <SaveErrorNotice error={save.error} fieldLabels={{ equipmentCode: t('equipment.codeLabel'), name: t('equipment.nameLabel'), calibrationValidUntil: t('equipment.validUntilLabel'), reason: t('common.reason') }} /> : null}
        </div>
      </Dialog>
    </>
  );
}
